import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { apiUpload, apiError } from '../../services/publicApi';
import { Plus, QrCode, Download, Trash2, Eye, ChevronDown, ChevronUp, MapPin, Calendar, Gift, Percent, X, BarChart3, Users, Target, Palette, Image, Upload, ExternalLink, Copy, Check, Sparkles, Filter, TrendingUp, Clock, RotateCcw, Files } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ScopeSelector from '../components/ScopeSelector';
import { PageTooltip } from '../components/TooltipSystem';
import { BrandColorPicker } from '../components/BrandColorPicker';

import { getBackendUrl } from '../../services/origin';
const FRONTEND_URL = getBackendUrl() || '';
const API = getBackendUrl() || '';

// Preview/QR URLs always use the current origin so they work both in preview
// (lazo-taco-staging.preview.emergentagent.com) and in production
// (ring.tacoloco.food) without needing a rebuild.
const customerOrigin = () => (typeof window !== 'undefined' ? window.location.origin : FRONTEND_URL);

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailAnalytics, setDetailAnalytics] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const heroFileRef = useRef(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Filters for the recap list
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Impossibile copiare il link');
    }
  };

  const DEFAULT_FORM = {
    name: '', slug: '', description: '', start_date: '', end_date: '',
    status: 'active', auto_publish: true,
    landing_title: 'Vieni a trovarci per scoprire il tuo premio!',
    landing_subtitle: '', address: '', maps_url: '', prize_expiry_days: 30,
    landing_hero_url: '', landing_cta_text: 'Sei gia registrato? Scopri il premio!',
    landing_footer_text: 'Se non sei ancora registrato, presentati al locale e ti registreremo!',
    landing_bg_color: '#111114', landing_accent_color: '',
    login_title: 'Scopri il tuo premio!',
    login_subtitle: 'Inserisci il tuo numero e PIN per rivelare il premio',
    login_bg_color: '#111114', login_accent_color: '',
    scratch_title: 'Gratta la card per scoprire il tuo premio!',
    scratch_bg_color: '#111114', scratch_accent_color: '',
    thankyou_title: 'Complimenti!',
    thankyou_subtitle: 'Mostra questa schermata in cassa per riscattare il tuo premio',
    thankyou_cta_text: 'Vai alla tua bacheca',
    thankyou_bg_color: '#111114', thankyou_accent_color: '',
    prizes: [{ coupon_id: '', probability: 100, max_quantity: null }]
  };

  const [form, setForm] = useState({...DEFAULT_FORM});
  const [scope, setScope] = useState('brand');

  const load = useCallback(async () => {
    try {
      const [campRes, coupRes] = await Promise.all([
        adminApi.get('/admin/campaigns'),
        adminApi.get('/admin/coupons?is_active=true&limit=100')
      ]);
      setCampaigns(campRes.data.campaigns || []);
      setCoupons(coupRes.data.coupons || []);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({...DEFAULT_FORM});
    setEditing(null);
    setShowForm(false);
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5MB)'); return; }
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const res = await apiUpload('/api/upload', fd, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status >= 200 && res.status < 300 && res.data?.url) {
        setForm(f => ({ ...f, landing_hero_url: res.data.url }));
        toast.success('Immagine hero caricata!');
      } else {
        toast.error(apiError(res));
      }
    } catch {
      toast.error('Errore upload immagine');
    } finally { setUploadingHero(false); }
  };

  const totalProb = form.prizes.reduce((s, p) => s + (Number(p.probability) || 0), 0);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Nome obbligatorio');
    if (form.prizes.length === 0) return toast.error('Almeno un premio');
    if (form.prizes.some(p => !p.coupon_id)) return toast.error('Seleziona un coupon per ogni premio');
    if (Math.abs(totalProb - 100) > 0.01) return toast.error(`La somma probabilità deve essere 100% (ora: ${totalProb}%)`);

    try {
      const payload = { ...form, scope };
      if (editing) {
        await adminApi.put(`/admin/campaigns/${editing}`, payload);
        toast.success('Campagna aggiornata');
      } else {
        await adminApi.post('/admin/campaigns', payload);
        toast.success('Campagna creata');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa campagna?')) return;
    try {
      await adminApi.delete(`/admin/campaigns/${id}`);
      toast.success('Campagna eliminata');
      load();
      if (detail?.id === id) setDetail(null);
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleClone = async (c) => {
    if (!window.confirm(`Clonare "${c.name}"? Verrà creata una copia in stato 'bozza' con date vuote, premi resettati e nessun dato di scansione/assegnazione.`)) return;
    try {
      const res = await adminApi.post(`/admin/campaigns/${c.id}/clone`);
      toast.success(`Campagna clonata: ${res.data.name}`);
      await load();
      // Open the cloned campaign in edit mode so user can set new dates
      openEdit(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore clonazione');
    }
  };

  const handleResetData = async (c) => {
    const prompt = window.prompt(
      `Stai per eliminare TUTTI i dati generati dalla campagna "${c.name}":\n` +
      `• premi assegnati ai clienti\n` +
      `• scansioni e analytics\n` +
      `• riferimenti pendenti sui clienti\n\n` +
      `La configurazione (premi, copy, date, immagini) rimane intatta.\n\n` +
      `Digita RESET per confermare:`
    );
    if (prompt !== 'RESET') {
      if (prompt !== null) toast.error('Conferma non valida. Digita esattamente RESET.');
      return;
    }
    try {
      const res = await adminApi.post(`/admin/campaigns/${c.id}/reset-data`);
      toast.success(`Reset completato: ${res.data.deleted_prizes} premi e ${res.data.deleted_scans} scansioni eliminati`);
      await load();
      if (detail?.id === c.id) {
        // Refresh detail panel
        const refreshed = await adminApi.get(`/admin/campaigns/${c.id}`);
        setDetail(refreshed.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore reset dati');
    }
  };

  const openEdit = (c) => {
    setForm({
      name: c.name, slug: c.slug || '', description: c.description || '',
      start_date: c.start_date || '', end_date: c.end_date || '',
      status: c.status || 'active',
      auto_publish: c.auto_publish !== false,
      landing_title: c.landing_title || '', landing_subtitle: c.landing_subtitle || '',
      address: c.address || '', maps_url: c.maps_url || '',
      prize_expiry_days: c.prize_expiry_days || 30,
      landing_hero_url: c.landing_hero_url || '',
      landing_cta_text: c.landing_cta_text || DEFAULT_FORM.landing_cta_text,
      landing_footer_text: c.landing_footer_text || DEFAULT_FORM.landing_footer_text,
      landing_bg_color: c.landing_bg_color || '#111114',
      landing_accent_color: c.landing_accent_color || '',
      login_title: c.login_title || DEFAULT_FORM.login_title,
      login_subtitle: c.login_subtitle || DEFAULT_FORM.login_subtitle,
      login_bg_color: c.login_bg_color || '#111114',
      login_accent_color: c.login_accent_color || '',
      scratch_title: c.scratch_title || DEFAULT_FORM.scratch_title,
      scratch_bg_color: c.scratch_bg_color || '#111114',
      scratch_accent_color: c.scratch_accent_color || '',
      thankyou_title: c.thankyou_title || DEFAULT_FORM.thankyou_title,
      thankyou_subtitle: c.thankyou_subtitle || DEFAULT_FORM.thankyou_subtitle,
      thankyou_cta_text: c.thankyou_cta_text || DEFAULT_FORM.thankyou_cta_text,
      thankyou_bg_color: c.thankyou_bg_color || '#111114',
      thankyou_accent_color: c.thankyou_accent_color || '',
      prizes: (c.prizes || []).map(p => ({
        coupon_id: p.coupon_id, probability: p.probability,
        max_quantity: p.max_quantity || null
      }))
    });
    setEditing(c.id);
    setScope(c.scope || 'brand');
    setShowForm(true);
  };

  const openDetail = async (id) => {
    try {
      const res = await adminApi.get(`/admin/campaigns/${id}`);
      setDetail(res.data);
      setDetailAnalytics(null);
      adminApi.get(`/admin/campaigns/${id}/analytics`)
        .then(r => setDetailAnalytics(r.data))
        .catch(() => toast.error('Impossibile caricare le analytics della campagna'));
    } catch { toast.error('Errore caricamento dettaglio'); }
  };

  const downloadQR = (campaignId, campaignName) => {
    const svg = document.getElementById(`qr-${campaignId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1200;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 1024, 1200);
      ctx.drawImage(img, 112, 60, 800, 800);
      ctx.fillStyle = '#111114';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(campaignName, 512, 940);
      ctx.font = '24px Arial';
      ctx.fillStyle = '#666';
      ctx.fillText('Scansiona per il tuo premio!', 512, 990);
      const link = document.createElement('a');
      link.download = `QR_${campaignName.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const statusBadge = (s) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-700',
      paused: 'bg-amber-100 text-amber-700',
      concluded: 'bg-slate-100 text-gray-500',
      draft: 'bg-blue-50 text-blue-600',
    };
    const labels = { active: 'Attiva', paused: 'In pausa', concluded: 'Conclusa', draft: 'Bozza' };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors[s] || colors.concluded}`}>{labels[s] || s}</span>;
  };

  const getCouponLabel = (id) => {
    const c = coupons.find(x => x.id === id);
    if (!c) return '—';
    if (c.coupon_type === 'discount') return `Sconto ${c.discount_percent}%`;
    if (c.coupon_type === 'bonus_points') return `+${c.bonus_points} punti`;
    if (c.coupon_type === 'prize') return c.prize_name || c.code;
    if (c.coupon_type === 'game_token') return `${c.tokens_amount} gettoni`;
    return c.code;
  };

  // ─── DETAIL VIEW ──────────────────────────────────────────────────────
  if (detail) {
    const slugOrId = detail.slug || detail.id;
    const qrUrl = `${customerOrigin()}/c/${slugOrId}`;
    return (
      <div className="space-y-6" data-testid="campaign-detail">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => setDetail(null)} className="text-sm text-[#64748B] hover:text-[#111] mb-1">&larr; Torna alle campagne</button>
            <h2 className="text-2xl font-bold text-[#1A1A1A]">{detail.name}</h2>
          </div>
          {statusBadge(detail.status)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard icon={<Target size={20} />} label="Scan QR" value={detail.total_scans} color="blue" />
          <StatCard icon={<Users size={20} />} label="Premi assegnati" value={detail.total_assignments} color="orange" />
          <StatCard icon={<Gift size={20} />} label="Riscattati" value={detail.total_redeemed} color="green" />
          <StatCard icon={<Percent size={20} />} label="Tasso riscatto" value={detail.total_assignments > 0 ? `${Math.round(detail.total_redeemed / detail.total_assignments * 100)}%` : '—'} color="purple" />
        </div>

        {/* QR Code & Link parlante */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="campaign-qr-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#111]">QR Code & Link Campagna</h3>
            {detail.slug ? (
              <span data-testid="slug-badge" className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1 border border-emerald-200">
                <Sparkles size={10} /> URL parlante personalizzabile
              </span>
            ) : null}
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="bg-white p-4 rounded-xl border flex-shrink-0">
              <QRCodeSVG id={`qr-${detail.id}`} value={qrUrl} size={200} level="H" />
            </div>
            <div className="space-y-3 flex-1 min-w-0 w-full">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">URL della landing</p>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <code data-testid="campaign-public-url" className="text-xs text-[#1A1A1A] font-mono truncate flex-1">{qrUrl}</code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(qrUrl)}
                    data-testid="copy-campaign-link-btn"
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${linkCopied ? 'bg-emerald-50 text-emerald-700' : 'bg-white border border-slate-200 hover:bg-slate-100 text-[#1A1A1A]'}`}
                  >
                    {linkCopied ? (<><Check size={12} /> Copiato</>) : (<><Copy size={12} /> Copia</>)}
                  </button>
                </div>
                {detail.slug ? (
                  <p className="text-[10px] text-[#64748B] mt-1.5">
                    Slug: <code className="font-mono text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{detail.slug}</code>
                    <span className="ml-1">— modificabile dal form di modifica.</span>
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-700 mt-1.5">⚠ Questa campagna usa un ID anziché uno slug. Modifica la campagna per assegnare un URL parlante.</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => downloadQR(detail.id, detail.name)}
                  data-testid="download-qr-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#b8411a] transition-colors"
                >
                  <Download size={16} /> Scarica QR (PNG)
                </button>
                <a
                  href={qrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-campaign-link-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-[#1A1A1A] rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink size={16} /> Apri landing
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Prize Pool */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="font-semibold text-[#111] mb-4">Pool Premi</h3>
          <div className="space-y-2">
            {(detail.prizes || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <div>
                  <span className="font-medium text-[#111]">{p.coupon_name || getCouponLabel(p.coupon_id)}</span>
                  <span className="ml-2 text-xs text-[#64748B]">({p.coupon_type})</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-bold text-[#D44E20]">{p.probability}%</span>
                  <span className="text-[#64748B]">{p.assigned_count || 0}{p.max_quantity ? `/${p.max_quantity}` : ''} assegnati</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <CampaignAnalyticsSection analytics={detailAnalytics} />

        {/* Assignments */}
        {detail.assignments?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="font-semibold text-[#111] mb-4">Ultimi premi assegnati</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#64748B] border-b">
                  <th className="pb-2">Cliente</th><th className="pb-2">Premio</th><th className="pb-2">Stato</th><th className="pb-2">Data</th>
                </tr></thead>
                <tbody>
                  {detail.assignments.map(a => (
                    <tr key={a.id} className="border-b border-gray-50">
                      <td className="py-2 font-medium">{a.customer_name}</td>
                      <td className="py-2">{a.prize_name}</td>
                      <td className="py-2">{a.status === 'redeemed' ? <span className="text-emerald-600 font-semibold">Riscattato</span> : a.status === 'expired' ? <span className="text-gray-400">Scaduto</span> : <span className="text-amber-600">Disponibile</span>}</td>
                      <td className="py-2 text-[#64748B]">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('it-IT') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── FORM ─────────────────────────────────────────────────────────────
  if (showForm) {
    return (
      <div className="space-y-6" data-testid="campaign-form">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#1A1A1A]">{editing ? 'Modifica Campagna' : 'Nuova Campagna'}</h2>
          <button onClick={resetForm} className="text-[#64748B] hover:text-[#111]"><X size={20} /></button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#333] block mb-1">Nome campagna *</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="es. Volantino Estivo 2026" data-testid="campaign-name-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#333] block mb-1">Descrizione</label>
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Breve descrizione" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#333] block mb-1">URL personalizzato (slug)</label>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 h-11">
              <span className="text-xs text-[#94A3B8] font-mono whitespace-nowrap">{customerOrigin().replace(/^https?:\/\//, '')}/c/</span>
              <input
                value={form.slug}
                onChange={e => setForm(f => ({...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')}))}
                placeholder="lascia vuoto per generare da nome"
                data-testid="campaign-slug-input"
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </div>
            <p className="text-[10px] text-[#94A3B8] mt-1">Sarà l'URL del QR code. Se lasci vuoto verrà generato automaticamente dal nome (es. "Estate 2026" → estate-2026). Deve essere unico per il brand.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#333] block mb-1">Data inizio</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#333] block mb-1">Data fine</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>

          {/* Status: live vs draft */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="campaign-status-toggle">
            <label className="text-sm font-medium text-[#333] block mb-2">Stato della campagna</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({...f, status: 'active'}))}
                data-testid="status-active-btn"
                className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                  form.status === 'active'
                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                    : 'bg-white border-slate-200 text-[#64748B] hover:border-slate-300'
                }`}
              >
                ⚡ Pubblica subito
                <span className="block font-normal text-[10px] mt-0.5 opacity-80">Visibile e scansionabile dai clienti</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({...f, status: 'draft'}))}
                data-testid="status-draft-btn"
                className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                  form.status === 'draft'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'bg-white border-slate-200 text-[#64748B] hover:border-slate-300'
                }`}
              >
                📝 Salva come bozza
                <span className="block font-normal text-[10px] mt-0.5 opacity-80">Non visibile ai clienti, attivabile dopo</span>
              </button>
            </div>

            {/* Auto-publish sub-control: only relevant when status='draft' */}
            {form.status === 'draft' && (
              <div className="mt-3 pt-3 border-t border-slate-200" data-testid="auto-publish-control">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.auto_publish !== false}
                    onChange={e => setForm(f => ({...f, auto_publish: e.target.checked}))}
                    data-testid="auto-publish-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#D44E20] cursor-pointer"
                  />
                  <span className="text-xs text-[#333] flex-1">
                    <span className="font-medium">Pubblica automaticamente alla data di inizio</span>
                    <span className="block text-[#64748B] text-[11px] mt-0.5">
                      {form.auto_publish !== false
                        ? `Quando arriverà ${form.start_date ? new Date(form.start_date).toLocaleDateString('it-IT') : 'la data di inizio'}, la campagna passerà automaticamente in stato "Attiva". Puoi sempre rimetterla in bozza dopo la modifica.`
                        : 'La campagna resta in bozza finché non la pubblichi manualmente cliccando "⚡ Pubblica subito".'}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Landing page config */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#111] flex items-center gap-2"><MapPin size={16} /> Landing Page (Step 1)</h3>
              {editing && <button onClick={() => window.open(`${customerOrigin()}/c/${editing}?step=landing&preview=true`, '_blank')} data-testid="preview-landing-step-btn" className="text-xs font-semibold text-[#D44E20] hover:underline flex items-center gap-1"><Eye size={14} /> Anteprima</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Titolo landing</label>
                <input value={form.landing_title} onChange={e => setForm(f => ({...f, landing_title: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Sottotitolo</label>
                <input value={form.landing_subtitle} onChange={e => setForm(f => ({...f, landing_subtitle: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Indirizzo locale</label>
                <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="Via Roma 1, Milano" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Link Google Maps</label>
                <input value={form.maps_url} onChange={e => setForm(f => ({...f, maps_url: e.target.value}))} placeholder="https://maps.google.com/..." className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          </div>

          {/* Landing page visual customization */}
          <div className="border-t pt-4">
            <h3 className="font-semibold text-[#111] mb-3 flex items-center gap-2"><Image size={16} /> Visual Landing Page</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#333] block mb-1">Immagine hero (banner in alto)</label>
                <input ref={heroFileRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
                <div className="flex items-center gap-3">
                  <button onClick={() => heroFileRef.current?.click()} disabled={uploadingHero} data-testid="upload-hero-btn" className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-[#333] hover:bg-slate-100 transition-colors disabled:opacity-50">
                    <Upload size={16} />
                    {uploadingHero ? 'Caricamento...' : 'Carica immagine'}
                  </button>
                  {form.landing_hero_url && (
                    <button onClick={() => setForm(f => ({...f, landing_hero_url: ''}))} className="text-xs text-red-400 hover:text-red-600">Rimuovi</button>
                  )}
                </div>
                {form.landing_hero_url && <img src={form.landing_hero_url} alt="Hero preview" className="mt-2 h-24 rounded-lg object-cover" />}
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Testo pulsante CTA</label>
                <input value={form.landing_cta_text} onChange={e => setForm(f => ({...f, landing_cta_text: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Testo footer</label>
                <input value={form.landing_footer_text} onChange={e => setForm(f => ({...f, landing_footer_text: e.target.value}))} className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore sfondo</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.landing_bg_color || '#FFFFFF'} onChange={v => setForm(f => ({...f, landing_bg_color: v}))} testid="landing-bg-color" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore accento (vuoto = brand)</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.landing_accent_color || ''} onChange={v => setForm(f => ({...f, landing_accent_color: v}))} testid="landing-accent-color" />
                </div>
              </div>
            </div>
          </div>

          {/* Login page customization */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#111] flex items-center gap-2"><Palette size={16} /> Pagina Login (Step 2)</h3>
              {editing && <button onClick={() => window.open(`${customerOrigin()}/c/${editing}?step=login&preview=true`, '_blank')} data-testid="preview-login-btn" className="text-xs font-semibold text-[#D44E20] hover:underline flex items-center gap-1"><Eye size={14} /> Anteprima</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Titolo pagina login</label>
                <input value={form.login_title} onChange={e => setForm(f => ({...f, login_title: e.target.value}))} data-testid="login-title-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Sottotitolo pagina login</label>
                <input value={form.login_subtitle} onChange={e => setForm(f => ({...f, login_subtitle: e.target.value}))} data-testid="login-subtitle-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore sfondo login</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.login_bg_color || '#FFFFFF'} onChange={v => setForm(f => ({...f, login_bg_color: v}))} testid="login-bg-color" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore accento login (vuoto = brand)</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.login_accent_color || ''} onChange={v => setForm(f => ({...f, login_accent_color: v}))} testid="login-accent-color" />
                </div>
              </div>
            </div>
          </div>

          {/* Scratch card customization */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#111] flex items-center gap-2"><Gift size={16} /> Pagina Scratch Card (Step 3)</h3>
              {editing && <button onClick={() => window.open(`${customerOrigin()}/c/${editing}?step=scratch&preview=true`, '_blank')} data-testid="preview-scratch-btn" className="text-xs font-semibold text-[#D44E20] hover:underline flex items-center gap-1"><Eye size={14} /> Anteprima</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#333] block mb-1">Titolo / Istruzioni scratch card</label>
                <input value={form.scratch_title} onChange={e => setForm(f => ({...f, scratch_title: e.target.value}))} data-testid="scratch-title-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore sfondo scratch</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.scratch_bg_color || '#FFFFFF'} onChange={v => setForm(f => ({...f, scratch_bg_color: v}))} testid="scratch-bg-color" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore accento scratch (vuoto = brand)</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.scratch_accent_color || ''} onChange={v => setForm(f => ({...f, scratch_accent_color: v}))} testid="scratch-accent-color" />
                </div>
              </div>
            </div>
          </div>

          {/* Thank You page customization */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#111] flex items-center gap-2"><Target size={16} /> Pagina Thank You (Step 4)</h3>
              {editing && <button onClick={() => window.open(`${customerOrigin()}/c/${editing}?step=thankyou&preview=true`, '_blank')} data-testid="preview-thankyou-btn" className="text-xs font-semibold text-[#D44E20] hover:underline flex items-center gap-1"><Eye size={14} /> Anteprima</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Titolo Thank You</label>
                <input value={form.thankyou_title} onChange={e => setForm(f => ({...f, thankyou_title: e.target.value}))} data-testid="thankyou-title-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Sottotitolo Thank You</label>
                <input value={form.thankyou_subtitle} onChange={e => setForm(f => ({...f, thankyou_subtitle: e.target.value}))} data-testid="thankyou-subtitle-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Testo pulsante "Vai alla bacheca"</label>
                <input value={form.thankyou_cta_text} onChange={e => setForm(f => ({...f, thankyou_cta_text: e.target.value}))} data-testid="thankyou-cta-input" className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore sfondo Thank You</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.thankyou_bg_color || '#FFFFFF'} onChange={v => setForm(f => ({...f, thankyou_bg_color: v}))} testid="thankyou-bg-color" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#333] block mb-1">Colore accento Thank You (vuoto = brand)</label>
                <div className="flex items-center gap-2">
                  <BrandColorPicker value={form.thankyou_accent_color || ''} onChange={v => setForm(f => ({...f, thankyou_accent_color: v}))} testid="thankyou-accent-color" />
                </div>
              </div>
            </div>
          </div>

          {/* Prize pool */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#111] flex items-center gap-2"><Gift size={16} /> Pool Premi</h3>
              <div className={`text-sm font-bold ${Math.abs(totalProb - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                Totale: {totalProb}%
              </div>
            </div>
            {/* Column headers */}
            <div className="flex items-center gap-3 px-3 mb-1">
              <div className="flex-1 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Coupon / Premio</div>
              <div className="w-24 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider text-center">Probabilità</div>
              <div className="w-20 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider text-center">Qtà Max</div>
              <div className="w-4" />
            </div>
            <div className="space-y-2">
              {form.prizes.map((p, i) => {
                const linkedCoupon = coupons.find(c => c.id === p.coupon_id);
                const isAgeRestricted = linkedCoupon && (linkedCoupon.min_age || 0) >= 18;
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <select value={p.coupon_id} onChange={e => {
                        const newP = [...form.prizes];
                        newP[i] = {...newP[i], coupon_id: e.target.value};
                        setForm(f => ({...f, prizes: newP}));
                      }} data-testid={`prize-coupon-select-${i}`} className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm focus:border-[#D44E20] focus:outline-none">
                        <option value="">Seleziona coupon...</option>
                        {coupons.map(c => (
                          <option key={c.id} value={c.id}>
                            {(c.min_age || 0) >= 18 ? '🔞 ' : ''}{getCouponLabel(c.id)} ({c.code})
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1 w-24">
                        <input type="number" value={p.probability} onChange={e => {
                          const newP = [...form.prizes];
                          newP[i] = {...newP[i], probability: Number(e.target.value)};
                          setForm(f => ({...f, prizes: newP}));
                        }} min="0" max="100" className="w-16 h-10 rounded-lg border border-slate-200 px-2 text-sm text-center focus:border-[#D44E20] focus:outline-none" />
                        <span className="text-sm text-[#64748B]">%</span>
                      </div>
                      <input type="number" value={p.max_quantity || ''} onChange={e => {
                        const newP = [...form.prizes];
                        newP[i] = {...newP[i], max_quantity: e.target.value ? Number(e.target.value) : null};
                        setForm(f => ({...f, prizes: newP}));
                      }} placeholder="∞" min="1" className="w-20 h-10 rounded-lg border border-slate-200 px-2 text-sm text-center focus:border-[#D44E20] focus:outline-none" />
                      {form.prizes.length > 1 ? (
                        <button onClick={() => setForm(f => ({...f, prizes: f.prizes.filter((_, j) => j !== i)}))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                      ) : <div className="w-4" />}
                    </div>
                    {isAgeRestricted && (
                      <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-2 py-1 mt-2">
                        🔞 Premio per maggiorenni ({linkedCoupon.min_age}+): i clienti minorenni non potranno estrarlo. La probabilità verrà ridistribuita sui premi rimanenti.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={() => setForm(f => ({...f, prizes: [...f.prizes, { coupon_id: '', probability: 0, max_quantity: null }]}))} data-testid="add-prize-btn" className="mt-3 text-sm text-[#D44E20] font-semibold hover:underline flex items-center gap-1">
              <Plus size={14} /> Aggiungi premio
            </button>
          </div>

          <div className="border-t pt-4">
            <label className="text-sm font-medium text-[#333] block mb-1">Scadenza premi (giorni dalla assegnazione)</label>
            <input type="number" value={form.prize_expiry_days} onChange={e => setForm(f => ({...f, prize_expiry_days: Number(e.target.value)}))} min="1" className="w-32 h-11 rounded-xl border border-slate-200 px-4 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Preview buttons — only when editing */}
          {editing && (
            <div className="flex flex-wrap gap-2 w-full mb-2">
              {[
                { key: 'landing', label: 'Landing', icon: <MapPin size={14} /> },
                { key: 'login', label: 'Login', icon: <Palette size={14} /> },
                { key: 'scratch', label: 'Scratch', icon: <Gift size={14} /> },
                { key: 'thankyou', label: 'Thank You', icon: <Target size={14} /> },
              ].map(s => (
                <button key={s.key} onClick={() => window.open(`${customerOrigin()}/c/${editing}?step=${s.key}&preview=true`, '_blank')} data-testid={`preview-${s.key}-btn`} className="flex items-center gap-1.5 px-4 py-2.5 border-2 border-[#D44E20] text-[#D44E20] rounded-xl text-sm font-semibold hover:bg-[#D44E20]/5 transition-colors">
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
          )}
          <ScopeSelector value={scope} onChange={setScope} />
          <button onClick={handleSave} data-testid="save-campaign-btn" className="px-6 py-3 bg-[#D44E20] text-white rounded-xl font-semibold hover:bg-[#b8411a] transition-colors">
            {editing ? 'Salva modifiche' : 'Crea campagna'}
          </button>
          <button onClick={resetForm} className="px-6 py-3 border border-slate-200 text-[#64748B] rounded-xl hover:bg-slate-50">Annulla</button>
        </div>
      </div>
    );
  }

  // ─── LIST ─────────────────────────────────────────────────────────────
  // Apply filters
  const filteredCampaigns = campaigns.filter(c => {
    if (statusFilter !== 'all' && (c.status || 'active') !== statusFilter) return false;
    if (fromDate) {
      const cEnd = c.end_date ? new Date(c.end_date) : null;
      const cStart = c.start_date ? new Date(c.start_date) : null;
      const from = new Date(fromDate);
      // Campaign overlaps [from, ...]: its end must be >= from (or null = ongoing)
      if (cEnd && cEnd < from) return false;
      // …or its start must be >= from when no end
      if (!cEnd && cStart && cStart < from) { /* still include: ongoing */ }
    }
    if (toDate) {
      const cStart = c.start_date ? new Date(c.start_date) : null;
      const to = new Date(toDate);
      if (cStart && cStart > to) return false;
    }
    return true;
  });

  const totals = filteredCampaigns.reduce(
    (acc, c) => ({
      scans: acc.scans + (c.total_scans || 0),
      assignments: acc.assignments + (c.total_assignments || 0),
      redeemed: acc.redeemed + (c.total_redeemed || 0),
    }),
    { scans: 0, assignments: 0, redeemed: 0 }
  );
  const overallConv = totals.scans > 0 ? Math.round(totals.assignments / totals.scans * 100) : 0;
  const overallRedeem = totals.assignments > 0 ? Math.round(totals.redeemed / totals.assignments * 100) : 0;

  const STATUS_PILLS = [
    { value: 'all', label: 'Tutte' },
    { value: 'active', label: 'Attive' },
    { value: 'paused', label: 'In pausa' },
    { value: 'draft', label: 'Bozza' },
    { value: 'concluded', label: 'Concluse' },
  ];
  const hasDateFilter = fromDate || toDate;
  const hasAnyFilter = statusFilter !== 'all' || hasDateFilter;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Campagne Fisiche</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestisci campagne di acquisizione con QR code e premi</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/admin/campaigns/history')} data-testid="storico-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:border-[#D44E20] hover:text-[#D44E20] transition-colors text-sm">
            <BarChart3 size={16} /> Storico
          </button>
          <button onClick={() => setShowForm(true)} data-testid="new-campaign-btn" className="flex items-center gap-2 px-5 py-2.5 bg-[#D44E20] text-white rounded-xl font-semibold hover:bg-[#b8411a] transition-colors text-sm">
            <Plus size={16} /> Nuova Campagna
          </button>
        </div>
      </div>

      {/* Recap strip */}
      {!loading && campaigns.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="campaigns-recap">
          <RecapCard label="Campagne" value={filteredCampaigns.length} hint={`su ${campaigns.length} totali`} accent="#1A1A1A" />
          <RecapCard label="Scan totali" value={totals.scans.toLocaleString('it-IT')} accent="#3B82F6" />
          <RecapCard label="Assegnati" value={totals.assignments.toLocaleString('it-IT')} accent="#D44E20" />
          <RecapCard label="Riscattati" value={totals.redeemed.toLocaleString('it-IT')} accent="#059669" />
          <RecapCard label="Conv · Redeem" value={`${overallConv}% · ${overallRedeem}%`} hint="scan→assegn. · assegn.→riscatto" accent="#8B5CF6" />
        </div>
      )}

      {/* Filters */}
      {!loading && campaigns.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid="campaigns-filters">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="flex gap-2 flex-wrap">
              {STATUS_PILLS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setStatusFilter(p.value)}
                  data-testid={`campaign-filter-${p.value}`}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                    statusFilter === p.value
                      ? 'bg-[#D44E20] text-white border-[#D44E20]'
                      : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-[#64748B] font-medium">Da</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="campaign-from-date"
                className="h-9 rounded-lg border border-slate-200 px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
              <label className="text-xs text-[#64748B] font-medium">A</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="campaign-to-date"
                className="h-9 rounded-lg border border-slate-200 px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
              {hasAnyFilter && (
                <button onClick={() => { setStatusFilter('all'); setFromDate(''); setToDate(''); }} data-testid="campaign-filters-reset"
                  className="h-9 px-3 rounded-lg border border-slate-200 text-xs text-[#64748B] hover:bg-slate-50 flex items-center gap-1">
                  <X size={12} /> Reset
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-[#64748B]">Caricamento...</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16">
          <QrCode size={48} className="mx-auto mb-4 text-[#ccc]" />
          <p className="text-[#64748B]">Nessuna campagna creata</p>
          <p className="text-sm text-[#aaa] mt-1">Crea la tua prima campagna di volantinaggio con QR code</p>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="campaigns-empty-filter">
          <Filter size={36} className="mx-auto mb-3 text-[#ccc]" />
          <p className="text-[#64748B] text-sm">Nessuna campagna corrisponde ai filtri selezionati</p>
          <button onClick={() => { setStatusFilter('all'); setFromDate(''); setToDate(''); }} className="mt-3 text-xs text-[#D44E20] font-semibold hover:underline">Resetta filtri</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampaigns.map(c => {
            const conv = c.total_scans > 0 ? Math.round((c.total_assignments || 0) / c.total_scans * 100) : 0;
            const redeem = c.total_assignments > 0 ? Math.round((c.total_redeemed || 0) / c.total_assignments * 100) : 0;
            return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid={`campaign-card-${c.id}`}
              onClick={() => openDetail(c.id)}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-[#111] text-base truncate">{c.name}</h3>
                  {c.description && <p className="text-xs text-[#64748B] mt-0.5 line-clamp-1">{c.description}</p>}
                </div>
                {statusBadge(c.status)}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-bold text-[#111]">{c.total_scans || 0}</div>
                  <div className="text-[10px] text-[#64748B]">Scan</div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-bold text-[#D44E20]">{c.total_assignments || 0}</div>
                  <div className="text-[10px] text-[#64748B]">Assegnati</div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-bold text-emerald-600">{c.total_redeemed || 0}</div>
                  <div className="text-[10px] text-[#64748B]">Riscattati</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-blue-50/60 rounded-lg px-2 py-1.5 flex items-center justify-between">
                  <span className="text-[10px] text-[#64748B]">Conv.</span>
                  <span className="text-xs font-bold text-blue-700">{conv}%</span>
                </div>
                <div className="bg-emerald-50/60 rounded-lg px-2 py-1.5 flex items-center justify-between">
                  <span className="text-[10px] text-[#64748B]">Redeem</span>
                  <span className="text-xs font-bold text-emerald-700">{redeem}%</span>
                </div>
              </div>

              {c.start_date && (
                <div className="flex items-center gap-1 text-xs text-[#64748B] mb-3">
                  <Calendar size={12} />
                  {new Date(c.start_date).toLocaleDateString('it-IT')}
                  {c.end_date && ` — ${new Date(c.end_date).toLocaleDateString('it-IT')}`}
                </div>
              )}

              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => openDetail(c.id)} data-testid={`view-campaign-${c.id}`} className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-slate-50 text-[#64748B] rounded-lg text-xs font-medium hover:bg-slate-100 transition-colors">
                  <BarChart3 size={14} /> Dashboard
                </button>
                <button onClick={() => openEdit(c)} className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-[#D44E20]/10 text-[#D44E20] rounded-lg text-xs font-medium hover:bg-[#D44E20]/20 transition-colors">
                  <QrCode size={14} /> Modifica
                </button>
                <button
                  onClick={() => handleClone(c)}
                  data-testid={`clone-campaign-${c.id}`}
                  title="Clona campagna (crea una copia in bozza con date vuote)"
                  className="h-9 w-9 flex items-center justify-center bg-blue-50 text-blue-500 rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors"
                >
                  <Files size={14} />
                </button>
                {c.status !== 'active' && (
                  <button
                    onClick={() => handleResetData(c)}
                    data-testid={`reset-campaign-${c.id}`}
                    title="Resetta i dati (premi assegnati, scansioni). Mantieni la configurazione."
                    className="h-9 w-9 flex items-center justify-center bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 hover:text-amber-700 transition-colors"
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
                <button onClick={() => handleDelete(c.id)} className="h-9 w-9 flex items-center justify-center bg-red-50 text-red-400 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Hidden QR for download */}
              <div style={{ position: 'absolute', left: '-9999px' }}>
                <QRCodeSVG id={`qr-${c.id}`} value={`${customerOrigin()}/c/${c.slug || c.id}`} size={800} level="H" />
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-[#D44E20]',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <PageTooltip page="campaigns" />
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${colors[color]}`}>{icon}</div>
      <div className="text-2xl font-bold text-[#111]">{value}</div>
      <div className="text-xs text-[#64748B]">{label}</div>
    </div>
  );
}

function RecapCard({ label, value, hint, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`recap-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: accent || '#1A1A1A' }}>{value}</div>
      {hint && <div className="text-[10px] text-[#64748B] mt-0.5">{hint}</div>}
    </div>
  );
}

// ─── PER-CAMPAIGN ANALYTICS (funnel + daily bars + heatmap + prize breakdown) ───
function CampaignAnalyticsSection({ analytics }) {
  if (!analytics) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="campaign-analytics-loading">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={18} className="text-[#64748B]" />
          <h3 className="font-semibold text-[#111]">Analytics campagna</h3>
        </div>
        <div className="h-24 flex items-center justify-center text-sm text-[#94A3B8]">Caricamento dati…</div>
      </div>
    );
  }

  const { funnel, daily_series: series = [], hourly_heatmap: heatmap = [], prize_distribution: prizes = [] } = analytics;
  const maxDaily = Math.max(1, ...series.map(d => Math.max(d.scans, d.assignments)));

  // Build 7x24 heatmap grid. Mongo $dayOfWeek returns 1=Sun..7=Sat; we map
  // to Italian Mon-first layout (0=Mon..6=Sun).
  const dayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  let maxHeat = 0;
  heatmap.forEach(h => {
    const mongoDow = h.day; // 1..7 (Sun..Sat)
    const rowIdx = (mongoDow + 5) % 7; // Sun(1)->6, Mon(2)->0, Sat(7)->5
    grid[rowIdx][h.hour] = h.count;
    if (h.count > maxHeat) maxHeat = h.count;
  });

  return (
    <div className="space-y-4" data-testid="campaign-analytics">
      {/* Funnel */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-[#D44E20]" />
          <h3 className="font-semibold text-[#111]">Funnel campagna</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FunnelStep label="Scan QR" value={funnel.scans} color="#3B82F6" />
          <FunnelStep label="Assegnati" value={funnel.assignments} color="#D44E20" hint={`${funnel.conversion_rate}% dai scan`} />
          <FunnelStep label="Rivelati" value={funnel.revealed} color="#8B5CF6" hint={`${funnel.reveal_rate}% assegn.`} />
          <FunnelStep label="Riscattati" value={funnel.redeemed} color="#059669" hint={`${funnel.redeem_rate}% assegn.`} />
          <FunnelStep label="Scaduti" value={funnel.expired} color="#94A3B8" />
        </div>
      </div>

      {/* Daily trend */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="campaign-daily-chart">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={18} className="text-[#64748B]" />
          <h3 className="font-semibold text-[#111]">Andamento ultimi 30 giorni</h3>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-[#64748B]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#3B82F6]" /> Scan</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#D44E20]" /> Assegnati</span>
          </div>
        </div>
        {series.length === 0 ? (
          <div className="h-28 flex items-center justify-center text-sm text-[#94A3B8]">Nessuna attività negli ultimi 30 giorni</div>
        ) : (
          <div className="flex items-end gap-1 h-32 overflow-x-auto">
            {series.map(d => (
              <div key={d.date} className="flex flex-col items-center justify-end min-w-[22px] group" title={`${d.date} · Scan: ${d.scans} · Assegn.: ${d.assignments}`}>
                <div className="flex items-end gap-[2px] h-28">
                  <div className="w-[8px] bg-[#3B82F6] rounded-t-sm transition-all" style={{ height: `${(d.scans / maxDaily) * 100}%`, minHeight: d.scans > 0 ? '3px' : '0' }} />
                  <div className="w-[8px] bg-[#D44E20] rounded-t-sm transition-all" style={{ height: `${(d.assignments / maxDaily) * 100}%`, minHeight: d.assignments > 0 ? '3px' : '0' }} />
                </div>
                <div className="text-[9px] text-[#94A3B8] mt-1 -rotate-45 origin-top-left whitespace-nowrap pl-2">
                  {d.date.slice(5)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hourly heatmap */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="campaign-heatmap">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-[#64748B]" />
          <h3 className="font-semibold text-[#111]">Heatmap scansioni per giorno × ora</h3>
        </div>
        {maxHeat === 0 ? (
          <div className="h-24 flex items-center justify-center text-sm text-[#94A3B8]">Nessuna scansione registrata</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div className="flex items-center mb-1 pl-10">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="w-[22px] text-center text-[9px] text-[#94A3B8]">{h % 3 === 0 ? h : ''}</div>
                ))}
              </div>
              {grid.map((row, rIdx) => (
                <div key={rIdx} className="flex items-center">
                  <div className="w-10 text-[10px] text-[#64748B] font-medium">{dayLabels[rIdx]}</div>
                  {row.map((cell, h) => {
                    const intensity = maxHeat > 0 ? cell / maxHeat : 0;
                    const bg = cell === 0
                      ? '#F1F5F9'
                      : `rgba(212, 78, 32, ${Math.max(0.15, intensity)})`;
                    return (
                      <div key={h} title={`${dayLabels[rIdx]} ore ${h}:00 · ${cell} scan`}
                        className="w-[22px] h-[22px] mx-[1px] my-[1px] rounded-[3px]"
                        style={{ backgroundColor: bg }} />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Prize distribution */}
      {prizes.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="campaign-prize-distribution">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={18} className="text-[#D44E20]" />
            <h3 className="font-semibold text-[#111]">Distribuzione premi estratti</h3>
          </div>
          <div className="space-y-2">
            {(() => {
              const maxCount = Math.max(1, ...prizes.map(p => p.count));
              return prizes.map(p => (
                <div key={p.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-[#1A1A1A]">{p.name}</span>
                    <span className="text-[#64748B] tabular-nums">
                      {p.count} estratti · <span className="text-emerald-600">{p.redeemed} riscattati ({p.redeem_rate}%)</span>
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D44E20] rounded-full transition-all" style={{ width: `${(p.count / maxCount) * 100}%` }} />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, value, hint, color }) {
  return (
    <div className="rounded-xl p-3 border border-slate-100" style={{ backgroundColor: `${color}10` }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1" style={{ color }}>{value ?? 0}</div>
      {hint && <div className="text-[10px] text-[#64748B] mt-0.5">{hint}</div>}
    </div>
  );
}
